import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { ArrowRight } from "lucide-react";

const SERVICES = ["private_driver", "limousine", "party_bus", "transit_bus"];
const TRIP_TYPES = ["one_way", "round_trip", "hourly", "multi_day"];

const fieldCls =
  "w-full h-12 bg-white/5 border border-white/15 rounded-lg px-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/50 transition-colors";

export const HeroBookingWidget = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [f, setF] = useState({
    trip_type: "one_way", service: "", date: "", time: "", passengers: 2,
    pickup: "", dropoff: "", pickup_place: null, dropoff_place: null,
  });
  const [errors, setErrors] = useState({ pickup: false, dropoff: false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Typing invalidates a previously selected place until a suggestion is chosen again.
  const onPickupType = (v) => setF((s) => ({ ...s, pickup: v, pickup_place: null }));
  const onDropoffType = (v) => setF((s) => ({ ...s, dropoff: v, dropoff_place: null }));

  const onPickupSelect = (p) => {
    setErrors((e) => ({ ...e, pickup: false }));
    setF((s) => ({ ...s, pickup: p ? p.formattedAddress : s.pickup, pickup_place: p || null }));
  };
  const onDropoffSelect = (p) => {
    setErrors((e) => ({ ...e, dropoff: false }));
    setF((s) => ({ ...s, dropoff: p ? p.formattedAddress : s.dropoff, dropoff_place: p || null }));
  };

  const toTrip = (place) => {
    if (!place) return null;
    return {
      address: place.formattedAddress,
      place_id: place.placeId,
      place_name: place.placeName,
      city: place.city,
      province: place.province,
      postal_code: place.postalCode,
      country: place.country,
      location_type: place.locationType,
      coords: { lat: place.latitude, lng: place.longitude },
      is_airport: place.locationType === "airport",
    };
  };

  const submit = (e) => {
    e.preventDefault();
    // Text present but no valid selection => block and show the message.
    const pickupInvalid = !!f.pickup.trim() && !f.pickup_place;
    const dropoffInvalid = !!f.dropoff.trim() && !f.dropoff_place;
    if (pickupInvalid || dropoffInvalid) {
      setErrors({ pickup: pickupInvalid, dropoff: dropoffInvalid });
      return;
    }

    const pickup = toTrip(f.pickup_place);
    const dropoff = toTrip(f.dropoff_place);
    const handoff = { pickup, dropoff };

    // Shareable basics in the URL; rich place data (coords/placeId/airport) via router state,
    // with a sessionStorage fallback so coordinates survive a hard page refresh.
    const q = new URLSearchParams();
    if (f.trip_type) q.set("trip_type", f.trip_type);
    if (f.service) q.set("service", f.service);
    if (f.date) q.set("date", f.date);
    if (f.time) q.set("time", f.time);
    if (f.passengers) q.set("passengers", f.passengers);
    if (pickup) q.set("pickup", pickup.address);
    if (dropoff) q.set("dropoff", dropoff.address);

    try { sessionStorage.setItem("autobus_hero_handoff", JSON.stringify(handoff)); } catch { /* ignore */ }
    navigate(`/book?${q.toString()}`, { state: { handoff } });
  };

  return (
    <form
      onSubmit={submit}
      data-testid="hero-booking-widget"
      className="w-full rounded-2xl border border-white/15 bg-black/60 backdrop-blur-xl p-5 sm:p-6 shadow-2xl"
    >
      <p className="text-xs uppercase tracking-luxe text-white/60 mb-4">{t("widget.title")}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] uppercase tracking-luxe text-white/50 mb-1.5">{t("widget.tripType")}</label>
          <Select value={f.trip_type} onValueChange={(v) => set("trip_type", v)}>
            <SelectTrigger data-testid="widget-trip-type" className={fieldCls}><SelectValue /></SelectTrigger>
            <SelectContent>
              {TRIP_TYPES.map((tt) => <SelectItem key={tt} value={tt}>{t(`booking.tt.${tt}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-luxe text-white/50 mb-1.5">{t("widget.service")}</label>
          <Select value={f.service} onValueChange={(v) => set("service", v)}>
            <SelectTrigger data-testid="widget-service" className={fieldCls}>
              <SelectValue placeholder={t("widget.anyService")} />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => <SelectItem key={s} value={s}>{t(`svc.${s}.title`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="sm:col-span-2">
          <label className="block text-[11px] uppercase tracking-luxe text-white/50 mb-1.5">{t("widget.pickup")}</label>
          <AddressAutocomplete
            testId="widget-pickup"
            value={f.pickup}
            placeholder={t("booking.addressPlaceholder")}
            onChange={onPickupType}
            onSelect={onPickupSelect}
          />
          {errors.pickup && (
            <p className="text-xs text-destructive mt-1.5" data-testid="widget-pickup-error">{t("ac.selectSuggestion")}</p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label className="block text-[11px] uppercase tracking-luxe text-white/50 mb-1.5">{t("widget.dropoff")}</label>
          <AddressAutocomplete
            testId="widget-dropoff"
            value={f.dropoff}
            placeholder={t("booking.addressPlaceholder")}
            onChange={onDropoffType}
            onSelect={onDropoffSelect}
          />
          {errors.dropoff && (
            <p className="text-xs text-destructive mt-1.5" data-testid="widget-dropoff-error">{t("ac.selectSuggestion")}</p>
          )}
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-luxe text-white/50 mb-1.5">{t("widget.date")}</label>
          <input data-testid="widget-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)}
            className={`${fieldCls} [color-scheme:dark]`} />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-luxe text-white/50 mb-1.5">{t("widget.time")}</label>
          <input data-testid="widget-time" type="time" value={f.time} onChange={(e) => set("time", e.target.value)}
            className={`${fieldCls} [color-scheme:dark]`} />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[11px] uppercase tracking-luxe text-white/50 mb-1.5">{t("widget.passengers")}</label>
          <input data-testid="widget-passengers" type="number" min="1" max="60" value={f.passengers}
            onChange={(e) => set("passengers", e.target.value)} className={fieldCls} />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" data-testid="widget-submit"
            className="w-full h-12 rounded-lg bg-white text-black hover:bg-white/85 font-medium">
            {t("widget.submit")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </form>
  );
};
